import { createRoot } from 'react-dom/client';
import { BudgetWidget } from './BudgetWidget';
import { EmbedChart } from './EmbedChart';
import { decodeEmbedConfig, isEmbedChartKind } from './utils/embedConfig';
import {
  hasDataTranslations,
  loadDataTranslations,
  resolveLang,
  setLang,
  watchLang,
  whenDataReady,
  type Lang,
} from './i18n';
import type { BudgetWidgetProps, FilterState, WidgetState } from './types';

// Export main component
export { BudgetWidget, EmbedChart };

// Export types
export type { BudgetWidgetProps, FilterState };

// For React embedders that mount BudgetWidget themselves: in English, await
// this before rendering, so data names (categories, chapters …) are translated.
export { loadDataTranslations };

// UMD helper for script tag usage. The language is set before the first
// render: every t() / formatter reads it at render time. In English the
// data-name translations (categories, chapters …, i18n/data.ts) are loaded
// first, so the first paint is already English. Without an explicit `lang`
// prop the widget follows the host page live (watchLang): a switch remounts it
// in the new language, keeping the view through stateCarry.
export function render(container: HTMLElement, props: BudgetWidgetProps) {
  const stateCarry: { current: WidgetState | null } = { current: null };
  let current: BudgetWidgetProps = { ...props, lang: props.lang ?? resolveLang(container) };
  const root = createRoot(container);
  const draw = () => {
    setLang(current.lang!);
    // Keyed by language and by whether the data names are in: either change
    // remounts, so no memoised label survives it.
    const key = `${current.lang}:${hasDataTranslations()}`;
    root.render(<BudgetWidget key={key} {...current} stateCarry={stateCarry} />);
  };
  let cancelPending = whenDataReady(current.lang!, current.apiBaseUrl, draw);
  const show = (lang: Lang) => {
    cancelPending();
    current = { ...current, lang };
    cancelPending = whenDataReady(lang, current.apiBaseUrl, draw);
  };

  let stopWatching = props.lang ? () => {} : watchLang(container, show);

  return {
    unmount: () => {
      cancelPending();
      stopWatching();
      root.unmount();
    },
    update: (newProps: Partial<BudgetWidgetProps>) => {
      // An explicit language from the embedder pins it: stop following the host.
      if (newProps.lang) {
        stopWatching();
        stopWatching = () => {};
      }
      const langChanged = newProps.lang !== undefined && newProps.lang !== current.lang;
      current = { ...current, ...newProps };
      if (langChanged) show(current.lang!);
      else draw();
    },
  };
}

export interface RenderEmbedOptions {
  /** Same-origin inside the frame, so '/api'. */
  apiBaseUrl: string;
  /** 'area' | 'bar'; anything else falls back to 'area'. */
  chart: string;
  /** The embed page's own query string, e.g. window.location.search. */
  search: string;
  /** Target of the "Zum vollständigen Haushaltstracker" link. */
  trackerUrl: string;
  /** Display language; the embed page passes its ?lang=. Falls back to resolveLang(). */
  lang?: Lang;
}

/**
 * Mounts a single locked-down chart for the /embed/budget/:chart page.
 * Called explicitly by that page rather than via the auto-mount below, so the
 * config can be passed in and no data attribute has to carry it.
 */
export function renderEmbed(container: HTMLElement, options: RenderEmbedOptions) {
  const lang = options.lang === 'en' || options.lang === 'de' ? options.lang : resolveLang(container);
  setLang(lang);
  const chart = isEmbedChartKind(options.chart) ? options.chart : 'area';
  const config = decodeEmbedConfig(options.search, chart);
  const root = createRoot(container);
  // Like render(): English waits for the data-name translations.
  const draw = () => root.render(
    <EmbedChart
      key={String(hasDataTranslations())}
      apiBaseUrl={options.apiBaseUrl}
      config={config}
      trackerUrl={options.trackerUrl}
    />,
  );
  const cancel = whenDataReady(lang, options.apiBaseUrl, draw);

  return {
    unmount: () => {
      cancel();
      root.unmount();
    },
  };
}

// Auto-initialize if data attribute is present. An optional data-lang
// ("de" | "en") on the container overrides window.language.
if (typeof window !== 'undefined') {
  const mountAll = () => {
    const containers = document.querySelectorAll('[data-budget-widget]');
    containers.forEach((container) => {
      const apiBaseUrl = container.getAttribute('data-api-url');
      if (apiBaseUrl) {
        render(container as HTMLElement, { apiBaseUrl });
      }
    });
  };

  // DOMContentLoaded alone would silently do nothing when the bundle is loaded
  // with `async`/`defer` or injected dynamically — by then the event has
  // already fired. Check readyState first so a late-arriving script still mounts.
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }
}
